import React, { useState, useEffect } from 'react';
import { HealthIndexResponse, HealthIndexLevel, VitalContribution } from '../types';
import { getPatientHealthIndex, calculatePatientHealthIndex } from '../services/api';

interface HealthIndexProps {
  patientId: string;
  compact?: boolean;
  showCalculateButton?: boolean;
  userRole?: string;
}

/**
 * Score display configuration for each health index level
 */
const SCORE_CONFIG: Record<HealthIndexLevel, {
  label: string;
  color: string;
  bgColor: string;
  ringColor: string;
  description: string;
}> = {
  0: {
    label: 'Stable',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    ringColor: 'ring-green-500',
    description: 'All vital signs within normal ranges',
  },
  1: {
    label: 'Minor',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    ringColor: 'ring-blue-500',
    description: 'Minor deviation - continue monitoring',
  },
  2: {
    label: 'Moderate',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    ringColor: 'ring-yellow-500',
    description: 'Moderate deviation - increased observation',
  },
  3: {
    label: 'Significant',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    ringColor: 'ring-orange-500',
    description: 'Clinical review recommended',
  },
  4: {
    label: 'Major',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    ringColor: 'ring-red-500',
    description: 'Urgent clinical review required',
  },
  5: {
    label: 'Critical',
    color: 'text-red-800',
    bgColor: 'bg-red-200',
    ringColor: 'ring-red-600',
    description: 'Emergency response required',
  },
};

/**
 * HealthIndex Component
 *
 * Displays the Visensia health index score for a patient.
 * Supports compact mode for inline display or full mode with details.
 */
const HealthIndex: React.FC<HealthIndexProps> = ({
  patientId,
  compact = false,
  showCalculateButton = false,
  userRole,
}) => {
  const [healthData, setHealthData] = useState<HealthIndexResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHealthIndex();
  }, [patientId]);

  const loadHealthIndex = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPatientHealthIndex(patientId);
      setHealthData(data);
    } catch (err) {
      setError('Failed to load health index');
      console.error('Error loading health index:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    try {
      setCalculating(true);
      setError(null);
      const data = await calculatePatientHealthIndex(patientId);
      setHealthData(data);
    } catch (err: any) {
      setError(err.response?.data?.reason || 'Failed to calculate health index');
      console.error('Error calculating health index:', err);
    } finally {
      setCalculating(false);
    }
  };

  const canCalculate = userRole && ['nurse', 'doctor', 'admin'].includes(userRole);

  if (loading) {
    return (
      <div className={`${compact ? 'inline-flex items-center' : 'p-4'}`}>
        <div className="animate-pulse flex items-center space-x-2">
          <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
          {!compact && <div className="h-4 w-24 bg-gray-200 rounded"></div>}
        </div>
      </div>
    );
  }

  // No score available
  if (!healthData?.score) {
    if (compact) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-500 bg-gray-100">
          No data
        </span>
      );
    }

    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Health Index</h3>
            <p className="text-sm text-gray-500 mt-1">
              {healthData?.message || 'No health index data available'}
            </p>
          </div>
          {showCalculateButton && canCalculate && (
            <button
              onClick={handleCalculate}
              disabled={calculating}
              className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {calculating ? 'Calculating...' : 'Calculate Now'}
            </button>
          )}
        </div>
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      </div>
    );
  }

  // The Visensia index is CONTINUOUS (0-5) and arrives from Postgres NUMERIC as a
  // string, so it must be coerced and banded before indexing SCORE_CONFIG. Indexing
  // directly with e.g. 0.3 yields undefined and crashes on config.bgColor.
  const score = Number(healthData.score.score);
  const level = (Number.isFinite(score)
    ? Math.min(5, Math.max(0, Math.round(score)))
    : 0) as HealthIndexLevel;
  const config = SCORE_CONFIG[level] ?? SCORE_CONFIG[0 as HealthIndexLevel];
  const display = healthData.display;

  // Compact mode - just show the score badge
  if (compact) {
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${config.bgColor} ${config.color}`}
        title={config.description}
      >
        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white mr-1.5 font-bold text-xs">
          {score.toFixed(1)}
        </span>
        {config.label}
      </span>
    );
  }

  // Full mode with details
  return (
    <div className={`rounded-lg p-4 border-2 ${config.bgColor} border-current ${config.color}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          {/* Score Circle */}
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center ring-4 ${config.ringColor} bg-white`}
          >
            <span className={`text-3xl font-bold ${config.color}`}>
              {Number.isFinite(Number(healthData.score.raw_response?.rawScore))
                ? Number(healthData.score.raw_response?.rawScore).toFixed(1)
                : score.toFixed(1)}
            </span>
          </div>

          {/* Score Info */}
          <div>
            <h3 className={`text-lg font-semibold ${config.color}`}>
              Health Index: {config.label}
              {healthData.score.raw_response?.isFinal && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                  Validated
                </span>
              )}
            </h3>
            <p className={`text-sm ${config.color} opacity-80`}>{config.description}</p>
            <div className="flex items-center space-x-3 mt-1 text-xs text-gray-600">
              <span>Confidence: {typeof display?.confidence === 'number' ? display.confidence.toFixed(0) : (healthData.score.confidence || 0)}%</span>
              <span>|</span>
              <span>
                Updated: {new Date(healthData.score.calculated_at).toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col items-end space-y-2">
          {(healthData.score.raw_response?.alert ?? score >= 3) && (
            <span className="px-2 py-1 bg-white rounded text-xs font-medium text-red-700">
              Action Required
            </span>
          )}
          {showCalculateButton && canCalculate && (
            <button
              onClick={handleCalculate}
              disabled={calculating}
              className="px-3 py-1 bg-white text-gray-700 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              {calculating ? 'Calculating...' : 'Recalculate'}
            </button>
          )}
        </div>
      </div>

      {/* Vital Signs */}
      {healthData.score.vital_contributions && healthData.score.vital_contributions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-current opacity-30">
          <h4 className={`text-sm font-medium ${config.color} mb-2`}>Vital signs</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {healthData.score.vital_contributions.map((contribution: VitalContribution) => (
              <VitalContributionBadge key={contribution.vital_type} contribution={contribution} />
            ))}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
};

/**
 * Individual vital contribution badge
 */
const VitalContributionBadge: React.FC<{ contribution: VitalContribution }> = ({ contribution }) => {
  const formatVitalType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatValue = (value: number, vitalType: string) => {
    // Use integers for these vital types (no decimals)
    const integerTypes = ['heart_rate', 'respiratory_rate', 'spo2', 'blood_pressure', 'systolic', 'diastolic'];
    if (integerTypes.includes(vitalType)) {
      return Math.round(value).toString();
    }
    // For other vitals, show one decimal place
    return value.toFixed(1);
  };

  return (
    <div
      className={`p-2 rounded text-xs ${
        contribution.flagged ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'
      }`}
      title={contribution.flag_reason || `${formatVitalType(contribution.vital_type)}: ${contribution.value} ${contribution.unit}`}
    >
      <div className="font-medium text-gray-700 truncate">
        {formatVitalType(contribution.vital_type)}
      </div>
      <div className={`${contribution.flagged ? 'text-red-600' : 'text-gray-600'}`}>
        {contribution.vital_type === 'blood_pressure' && contribution.systolic && contribution.diastolic
          ? `${Math.round(contribution.systolic)}/${Math.round(contribution.diastolic)} ${contribution.unit}`
          : `${typeof contribution.value === 'number' ? formatValue(contribution.value, contribution.vital_type) : '0'} ${contribution.unit}`}
      </div>
      {contribution.flagged && (
        <div className="text-red-600 mt-0.5">!</div>
      )}
    </div>
  );
};

export default HealthIndex;
